import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";

const navItems = [
  { to: "/", label: "Accueil", testid: "accueil" },
  { to: "/simulateur-ia", label: "Simulateur IA", testid: "simulateur-ia" },
  { to: "/solutions", label: "Solutions Pro", testid: "solutions-pro" },
  { to: "/particuliers", label: "Particuliers", testid: "particuliers" },
  { to: "/financement-cee", label: "Financement CEE", testid: "financement-cee" },
  { to: "/realisations", label: "Réalisations", testid: "réalisations" },
  { to: "/blog", label: "Actualités", testid: "actualites" },
  { to: "/a-propos", label: "À propos", testid: "à-propos" },
  { to: "/contact", label: "Contact", testid: "contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  const transparent = isHome && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-8 inset-x-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent"
          : "bg-white/85 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center group">
          <Logo variant="dark" />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.testid}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-[13px] font-medium tracking-wide transition-all ${
                  isActive
                    ? "text-[#064E3B] bg-[#10B981]/10"
                    : "text-[#0F172A] hover:text-[#064E3B] hover:bg-black/[0.03]"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center">
          <Link to="/eligibilite" data-testid="header-cta-audit">
            <button className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition-all hover:-translate-y-0.5 bg-[#0055A4] text-white hover:bg-[#003D7A] shadow-[0_10px_24px_-10px_rgba(0,85,164,0.6)]">
              Audit énergétique gratuit
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </button>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          className={`xl:hidden h-10 w-10 grid place-items-center rounded-xl transition-colors ${
            transparent ? "bg-white/10 backdrop-blur border border-white/20 text-white" : "border border-black/10 text-[#0F172A]"
          }`}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-white border-t border-black/5 max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-6 flex flex-col gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.testid}`}
                className={({ isActive }) =>
                  `py-3 px-4 rounded-lg text-base font-medium ${isActive ? "bg-[#10B981]/10 text-[#064E3B]" : "text-[#0F172A]"}`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/eligibilite"
              data-testid="mobile-cta-audit"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#0055A4] text-white font-semibold py-3.5"
            >
              Audit énergétique gratuit <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
